// src/components/SmartSearch.jsx
// Enhanced Smart Search with tabbed navigation and better UX
// Tab-based interface for organized content viewing

import React, { useState } from 'react';
import { Loader2, Search, Sparkles, Info } from 'lucide-react';
import './css/SmartSearch.css';

const GEMINI_API_KEYS = [
  "AIzaSyCGWdeUXer_xGrjpc6ACiuDR2p_qHyfacI",
  "AIzaSyA3e-pfx5HCWpVL6JIGtKMjRsE1Gd4r3FE",
  "AIzaSyDg974T8YWZvLocwRh_DWxdvLGQmkutf7I"
];

const SmartSearch = ({ product }) => {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [aiError, setAiError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Parse AI response into structured sections
  const parseAIResponse = (text) => {
    const sections = [];
    const sectionRegex = /([🧾⚙️💬💡🧩🔥✨🎯🌟🚀📊🎁🏆🔮])\s*\*\*(.*?)\*\*\n([\s\S]*?)(?=\n[🧾⚙️💬💡🧩🔥✨🎯🌟🚀📊🎁🏆🔮]\s*\*\*|$)/g;
    
    let match;
    while ((match = sectionRegex.exec(text)) !== null) {
      const [, emoji, title, content] = match;
      sections.push({
        emoji,
        title: title.trim(),
        content: content.trim()
      });
    }

    // If no sections found, create a single section
    if (sections.length === 0) {
      sections.push({
        emoji: '✨',
        title: 'Analyse',
        content: text
      });
    }

    return sections;
  };

  // Format content with bold text
  const formatContent = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="smartsearch-highlight">$1</strong>')
      .replace(/\n\n/g, '</p><p class="smartsearch-paragraph">')
      .replace(/\n/g, '<br>');
  };

  // Smart Search Handler with fallback API keys
  const handleSmartSearch = async () => {
    if (!product) return;
    
    setAiLoading(true);
    setAiError('');
    setAiData(null);
    setActiveTab(0);
    
    const cacheKey = `smart-search-${product.id}`;
    localStorage.removeItem(cacheKey);
    
    try {
      const randomSeed = Math.random().toString(36).substring(7);
      const shortDesc = (product.shortDescription || '').substring(0, 200) + (product.shortDescription && product.shortDescription.length > 200 ? '...' : '');
      const longDesc = (product.longDescription || '').substring(0, 500) + (product.longDescription && product.longDescription.length > 500 ? '...' : '');
      
      const prompt = `Analyze this product deeply and provide highly encouraging, positive, sales-driven insights to convince users why this is an absolute must-buy that will transform their lives for the better. Focus on emotional benefits, problem-solving, joy it brings, and urgency to purchase now. Vary your response with fresh perspectives each time (seed: ${randomSeed}).

Product Name: ${product.name}
Category ID: ${product.categoryId} (assume a fitting category like 'Electronics' if unclear)
Short Description: ${shortDesc}
Long Description: ${longDesc}

Respond EXACTLY in this structured format using markdown for bold titles (**Title**). Write each section as a compelling paragraph (no bullets). Be enthusiastic, use persuasive language, highlight unique selling points, customer success stories (fabricated positively), and end with a strong call-to-action nudge. Keep sections concise yet vivid: Limit each paragraph to 2-4 sentences and 50-100 words max for easy reading.

🧾 **Description**
A thrilling paragraph overview of features, benefits, and why this product stands out as the best choice – make it irresistible!

⚙️ **Usage / Real-world use cases**
A detailed paragraph describing practical, everyday scenarios, advanced/creative uses, versatility, and fun applications tailored to the category, emphasizing instant value, ease, convenience, and innovation.

💬 **Customer sentiment (what people love & minor suggestions)**
**Loves:** A glowing paragraph on what customers rave about, including emotional impacts, with quote-like examples building trust and FOMO (keep 80% positive). **Suggestions:** A brief paragraph framing minor tweaks positively as opportunities for even greater perfection, showing the brand's commitment to excellence.

💡 **Pro Tips & Compatibility Hacks**
An empowering paragraph with insider tips to maximize performance, smart pairings/add-ons (suggest related products if fitting), and advice making users feel savvy and ready to enhance their experience right away.

🧩 **Why This Beats Alternatives**
A persuasive paragraph comparing to 2-3 competitors briefly, then explaining why this crushes them in quality, price, features, and positioning it as the premium, regret-free pick that reinforces uniqueness.

Finish with: "Grab yours today – limited stock won't last! Your satisfaction is guaranteed."`;

      let response;
      let data;
      let success = false;

      for (const apiKey of GEMINI_API_KEYS) {
        try {
          response = await fetch(
            `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                contents: [{
                  role: "user",
                  parts: [{
                    text: prompt
                  }]
                }],
                generationConfig: {
                  temperature: 0.9,
                  topK: 40,
                  topP: 0.95,
                  maxOutputTokens: 2500,
                },
              }),
            }
          );

          if (response.ok) {
            data = await response.json();
            
            if (!data.error) {
              const candidate = data.candidates?.[0];
              if (candidate && candidate.content) {
                const part = candidate.content.parts?.[0];
                if (part && part.text && part.text.trim()) {
                  success = true;
                  break;
                }
              }
            }
          }
        } catch (keyErr) {
          console.warn('API key failed', keyErr);
        }
      }

      if (!success) {
        throw new Error(`Impossible de terminer l'analyse. Veuillez reessayer.`);
      }
      
      const trimmedText = data.candidates[0].content.parts[0].text.trim();
      const sections = parseAIResponse(trimmedText);
      
      const aiResponse = { 
        text: trimmedText,
        sections: sections
      };
      
      setAiData(aiResponse);
      localStorage.setItem(cacheKey, JSON.stringify(aiResponse));
      
    } catch (err) {
      console.error('Gemini API error:', err);
      setAiError(err.message || "Impossible de terminer la recherche. Veuillez reessayer.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="smartsearch-container">
      {/* Smart Search Button */}
      <button
        onClick={handleSmartSearch}
        disabled={aiLoading}
        className="smartsearch-btn"
      >
        {aiLoading ? (
          <>
            <Loader2 className="smartsearch-btn-icon smartsearch-spin" size={20} />
            Analyse en cours...
          </>
        ) : (
          <>
            <Sparkles className="smartsearch-btn-icon" size={20} />
            Recherche intelligente
          </>
        )}
      </button>

      {/* Loading State */}
      {aiLoading && (
        <div className="smartsearch-loading">
          <Loader2 className="smartsearch-loading-icon smartsearch-spin" size={28} />
          <span className="smartsearch-loading-text">Analyse du produit par l'IA</span>
          <span className="smartsearch-loading-subtext">Cela peut prendre quelques secondes...</span>
        </div>
      )}

      {/* Error State */}
      {aiError && (
        <div className="smartsearch-error">
          <div className="smartsearch-error-icon">⚠️</div>
          <p className="smartsearch-error-text">{aiError}</p>
          <button onClick={handleSmartSearch} className="smartsearch-retry">
            Reessayer
          </button>
        </div>
      )}

      {/* Result with Tabs */}
      {aiData && !aiLoading && (
        <div className="smartsearch-result">
          {/* Header */}
          <div className="smartsearch-result-header">
            <Info className="smartsearch-result-icon" size={22} />
            <h3 className="smartsearch-result-title">Analyses intelligentes</h3>
          </div>

          {/* Tab Navigation */}
          <div className="smartsearch-tabs">
            <div className="smartsearch-tabs-scroll">
              {aiData.sections.map((section, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  className={`smartsearch-tab ${activeTab === idx ? 'smartsearch-tab-active' : ''}`}
                >
                  <span className="smartsearch-tab-emoji">{section.emoji}</span>
                  <span className="smartsearch-tab-title">{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="smartsearch-tab-content">
            {aiData.sections.map((section, idx) => (
              <div
                key={idx}
                className={`smartsearch-tab-panel ${activeTab === idx ? 'smartsearch-tab-panel-active' : ''}`}
              >
                <div className="smartsearch-tab-panel-inner">
                  <div className="smartsearch-content-header">
                    <span className="smartsearch-content-emoji">{section.emoji}</span>
                    <h4 className="smartsearch-content-title">{section.title}</h4>
                  </div>
                  <div 
                    className="smartsearch-content-body"
                    dangerouslySetInnerHTML={{ 
                      __html: '<p class="smartsearch-paragraph">' + formatContent(section.content) + '</p>'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Tab Navigation Indicators */}
          <div className="smartsearch-tab-indicators">
            {aiData.sections.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`smartsearch-indicator ${activeTab === idx ? 'smartsearch-indicator-active' : ''}`}
                aria-label={`Aller a la section ${idx + 1}`}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="smartsearch-result-footer">
            <span className="smartsearch-result-footer-icon">🤖</span>
            <span>Analyses generees par l'IA - verifier avant achat</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartSearch;
